import {
	Box,
	IconButton,
	Paper,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { EditableTimeCell } from "./EditableTimeCell";
import {
	colBorderSx,
	DAYS_ORDER,
	ERROR_BG,
	TelehealthSchedulePrompt,
	groupBorderSx,
	headerCellSx,
	PROMPTING_BG,
	TelehealthSchedule,
	TelehealthType
} from "./types";

interface GlobalHoursTableProps {
	clinicId: number;
	schedules: TelehealthSchedule[];
	prompts: TelehealthSchedulePrompt[];
	onAddSchedule: (day: string, telehealthType: TelehealthType) => void;
	onRemoveSchedule: (id: number) => void;
	onScheduleChange: (
		slotId: number,
		field: "startTime" | "endTime",
		value: string
	) => void;
	onPromptChange: (
		prompt: TelehealthSchedulePrompt,
		field: "allowTelehealth" | "waitTimePrompt" | "cancellationPrompt",
		value: boolean
	) => void;
	invalidScheduleIds: Set<number>;
	validationMessage: string | null;
	localOverlapDays: Set<string>;
}

export const GlobalHoursTable = ({
	clinicId,
	schedules,
	prompts,
	onAddSchedule,
	onRemoveSchedule,
	onScheduleChange,
	onPromptChange,
	invalidScheduleIds,
	validationMessage,
	localOverlapDays
}: GlobalHoursTableProps) => {
	// useEffect(() => {
	// 	console.info("GlobalHoursTable schedules", schedules, "prompting", prompts);
	// }, [schedules, prompts]);

	return (
		<>
			<TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell rowSpan={2} sx={{ ...headerCellSx, ...colBorderSx }}>
								Day
							</TableCell>
							<TableCell rowSpan={2} sx={{ ...headerCellSx, ...colBorderSx }}>
								From
							</TableCell>
							<TableCell rowSpan={2} sx={{ ...headerCellSx, ...colBorderSx }}>
								To
							</TableCell>
							<TableCell
								colSpan={2}
								align="center"
								sx={{
									...headerCellSx,
									...groupBorderSx,
									bgcolor: PROMPTING_BG
								}}
							>
								Telehealth wait-time prompting and cancellation prompting
							</TableCell>
						</TableRow>
						<TableRow>
							{/* <TableCell
								sx={{
									...headerCellSx,
									...groupBorderSx,
									bgcolor: PROMPTING_BG
								}}
							>
								Telehealth
							</TableCell> */}
							<TableCell sx={{ ...headerCellSx, bgcolor: PROMPTING_BG }}>
								Wait-time prompting
							</TableCell>
							<TableCell sx={{ ...headerCellSx, bgcolor: PROMPTING_BG }}>
								Cancellation prompting
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{DAYS_ORDER.map((day) => {
							const daySchedules = schedules.filter((s) => s.dayOfWeek === day);
							const dayPrompt = prompts.find((h) => h.dayOfWeek === day) ?? {
								id: 0,
								clinicId,
								dayOfWeek: day,
								allowTelehealth: false,
								waitTimePrompt: false,
								cancellationPrompt: false,
								telehealthType: TelehealthType.Global
							};
							const conflict = localOverlapDays.has(day);
							const rowCount = daySchedules.length;

							const dayCellContent = (
								<Box
									display="flex"
									alignItems="center"
									justifyContent="space-between"
									minWidth={90}
								>
									{day}
									<IconButton
										size="small"
										onClick={() => onAddSchedule(day, TelehealthType.Global)}
										sx={{
											ml: 0.5,
											p: 0.25
										}}
									>
										<AddIcon fontSize="small" />
									</IconButton>
								</Box>
							);

							const switchCells = (
								<>
									{/* <TableCell
										rowSpan={rowCount || 1}
										sx={{ ...groupBorderSx, bgcolor: PROMPTING_BG }}
									>
										<Switch
											size="small"
											checked={dayPrompt.allowTelehealth}
											onChange={(e) =>
												onPromptChange(
													dayPrompt,
													"allowTelehealth",
													e.target.checked
												)
											}
										/>
									</TableCell> */}
									<TableCell
										rowSpan={rowCount || 1}
										sx={{ bgcolor: PROMPTING_BG }}
									>
										<Switch
											size="small"
											checked={dayPrompt.waitTimePrompt}
											onChange={(e) =>
												onPromptChange(
													dayPrompt,
													"waitTimePrompt",
													e.target.checked
												)
											}
										/>
									</TableCell>
									<TableCell
										rowSpan={rowCount || 1}
										sx={{ bgcolor: PROMPTING_BG }}
									>
										<Switch
											size="small"
											checked={dayPrompt.cancellationPrompt}
											onChange={(e) =>
												onPromptChange(
													dayPrompt,
													"cancellationPrompt",
													e.target.checked
												)
											}
										/>
									</TableCell>
								</>
							);

							if (daySchedules.length === 0) {
								return (
									<TableRow key={day}>
										<TableCell sx={colBorderSx}>{dayCellContent}</TableCell>
										<TableCell sx={colBorderSx}></TableCell>
										<TableCell sx={colBorderSx}></TableCell>
										{switchCells}
									</TableRow>
								);
							}

							return daySchedules.map((schedule, index) => {
								const hasValidationError = invalidScheduleIds.has(schedule.id);
								return (
									<TableRow
										key={schedule.id}
										hover={!conflict && !hasValidationError}
										sx={
											conflict || hasValidationError
												? { bgcolor: ERROR_BG }
												: {}
										}
									>
										{index === 0 && (
											<TableCell rowSpan={rowCount} sx={colBorderSx}>
												{dayCellContent}
											</TableCell>
										)}
										<EditableTimeCell
											sx={colBorderSx}
											value={schedule.startTime}
											onChange={(v) =>
												onScheduleChange(schedule.id, "startTime", v)
											}
										/>
										<EditableTimeCell
											sx={colBorderSx}
											value={schedule.endTime}
											onChange={(v) =>
												onScheduleChange(schedule.id, "endTime", v)
											}
											endAdornment={
												<IconButton
													size="small"
													onClick={() => onRemoveSchedule(schedule.id)}
													sx={{
														p: 0.25
													}}
												>
													<DeleteOutlineIcon fontSize="small" />
												</IconButton>
											}
										/>
										{index === 0 && switchCells}
									</TableRow>
								);
							});
						})}
					</TableBody>
				</Table>
			</TableContainer>
			{validationMessage && (
				<Typography color="error" mb={4} fontSize="0.95rem">
					{validationMessage}
				</Typography>
			)}
		</>
	);
};
